import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { maskMoneyInput, moneyToInput, parseInputToApi, formatMoney, formatDate } from '@/lib/money';
import { cardCycleFor } from '@/lib/cardCycle';
import type { ReferenceData, Transaction } from './types';
import { CategorySelect } from '@/features/categories/CategorySelect';
import { AttachmentPanel } from './AttachmentPanel';

/**
 * Formulário de lançamento — RF-LAN-01.
 *
 * O valor líquido exibido aqui é PRÉVIA. Quem calcula de verdade é o servidor
 * (RN-03): enviar `net_amount` é recusado com 422. Se a prévia divergisse do
 * gravado, o número na tela seria mentira — por isso a fórmula é a mesma.
 */
export function TransactionForm({
  transaction,
  reference,
  selectedIds,
  presetCardId,
  onClose,
  onSaved,
}: {
  /** Nulo cria; preenchido edita. */
  transaction?: Transaction | null;
  reference: ReferenceData;
  selectedIds: number[];
  /**
   * Cartão já escolhido — usado quando o formulário abre pela tela de Cartões.
   *
   * O mesmo formulário serve aos dois caminhos de propósito. Um segundo
   * formulário só para cartão precisaria repetir categoria, parcelas, desconto
   * e contato, e os dois inevitavelmente iriam divergir com o tempo — a versão
   * menos usada ficaria para trás sem ninguém notar.
   */
  presetCardId?: number | null;
  onClose: () => void;
  onSaved: (warnings?: string[]) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const isEdit = transaction != null;

  /** Já tem dinheiro baixado? Governa o que a edição pode fazer. */
  const isSettled = isEdit && Number(transaction.settled_amount) > 0;

  const [type, setType] = useState<'receita' | 'despesa'>(transaction?.type ?? 'despesa');
  const [description, setDescription] = useState(transaction?.description ?? '');
  const [entityId, setEntityId] = useState<number>(
    transaction?.entity_id
      ?? (presetCardId != null
        ? (reference.cards.find((c) => c.id === presetCardId)?.entity_id
          ?? reference.entities[0]?.id ?? 0)
        : reference.entities[0]?.id ?? 0),
  );
  const [competenceDate, setCompetenceDate] = useState(
    transaction?.competence_date?.slice(0, 10) ?? today,
  );
  const [dueDate, setDueDate] = useState(transaction?.due_date?.slice(0, 10) ?? today);
  const [grossMasked, setGrossMasked] = useState(
    transaction ? moneyToInput(transaction.gross_amount) : '',
  );
  const [discountMasked, setDiscountMasked] = useState(
    transaction && Number(transaction.discount) > 0 ? moneyToInput(transaction.discount) : '',
  );
  const [categoryId, setCategoryId] = useState<string>(
    transaction?.category_id?.toString() ?? '',
  );
  const [accountId, setAccountId] = useState<string>(transaction?.account_id?.toString() ?? '');
  const [cardId, setCardId] = useState<string>(
    transaction?.credit_card_id?.toString() ?? presetCardId?.toString() ?? '',
  );
  const [partyId, setPartyId] = useState<string>(transaction?.party_id?.toString() ?? '');

  /**
   * O usuário já mexeu no vencimento à mão?
   *
   * Governa a sugestão de prazo. Sugestão que sobrescreve uma data digitada
   * de propósito é pior que sugestão nenhuma: o número muda sozinho depois
   * de a pessoa já ter decidido, e ela não tem como saber o que aconteceu.
   */
  const [dueTouched, setDueTouched] = useState(isEdit);
  const [installments, setInstallments] = useState(1);
  const [notes, setNotes] = useState(transaction?.notes ?? '');
  const [advanced, setAdvanced] = useState(false);

  /**
   * Motivo — obrigatório só quando o lançamento já tem baixa (CA-05).
   *
   * Exigir sempre transformaria a justificativa em ruído digitado no
   * automático. O caso que importa é outro: alterar o valor de algo já pago
   * mexe num período que pode estar conciliado, e aí o "por quê" é a única
   * coisa que explica a diferença meses depois.
   */
  const [reason, setReason] = useState('');

  /**
   * Contatos vêm de `/parties`, e não do `/reference`: a lista pode ser longa
   * demais para entrar num payload carregado em toda tela.
   */
  const partyList = useQuery({
    queryKey: ['parties', 'options', selectedIds],
    queryFn: () =>
      api.get<{ data: Array<{ id: number; name: string; default_payment_term_days: number | null }> }>(
        '/parties?per_page=200',
        { entityIds: selectedIds },
      ),
    select: (r) => r.data,
    staleTime: 5 * 60_000,
  });

  const parties = partyList.data ?? [];
  const party = parties.find((p) => String(p.id) === partyId);
  const termDays = party?.default_payment_term_days ?? null;

  /**
   * Vencimento sugerido pelo prazo do contato — RF-CAD-01.
   *
   * O prazo é uma característica do CONTATO, não do lançamento: "este
   * fornecedor sempre dá 30 dias". Guardá-lo e nunca usá-lo — que era o caso —
   * transforma o campo numa promessa que a tela não cumpre.
   *
   * Conta a partir da data de COMPETÊNCIA, não de hoje: "30 dias" num boleto
   * significa 30 dias da compra ou da nota, e não 30 dias de quando alguém
   * lembrou de lançar.
   */
  function suggestDueDate(base: string, days: number): string {
    const d = new Date(`${base}T12:00:00`);   // meio-dia evita virar o dia por fuso
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
  }

  const suggestion =
    termDays !== null && competenceDate ? suggestDueDate(competenceDate, termDays) : null;

  const gross = parseInputToApi(grossMasked);
  const discount = parseInputToApi(discountMasked || '0');

  // Prévia com a mesma fórmula do servidor (RN-03)
  const previewNet = (Number(gross) - Number(discount)).toFixed(2);

  /**
   * AS CONTAS DAS OUTRAS ENTIDADES TAMBÉM ENTRAM — RF-LAN-05, RN-13.
   *
   * "Fiz o pagamento de uma conta e escolhi que foi pago por outra entidade
   * consolidada. Depois fui alterar o Pago com para outro banco, uma correção
   * de onde paguei, e não aparecem todas as entidades."
   *
   * A tela de baixa já oferecia as contas de outras entidades — pagar a conta
   * da empresa do bolso da PF acontece toda semana. Este formulário não, e o
   * resultado é que dava para REGISTRAR a coisa e não dava para CORRIGIR.
   *
   * As da própria entidade continuam primeiro e soltas; as outras vão num grupo
   * separado, com o nome de quem é. O caso comum não pode ficar mais difícil
   * por causa do raro.
   */
  const accountsOfEntity = reference.accounts.filter((a) => a.entity_id === entityId);
  const accountsOfOthers = reference.accounts.filter((a) => a.entity_id !== entityId);

  /**
   * CARTÃO CONTINUA SÓ DA ENTIDADE, e não é esquecimento.
   *
   * Conta de outro é dinheiro emprestado — o saldo entre as entidades registra
   * isso e a transferência de reembolso zera. Cartão de outro é diferente: a
   * compra entraria na fatura de quem não a fez, e essa fatura é uma obrigação
   * com o banco, não um acerto entre duas pessoas.
   */
  const cardsOfEntity = reference.cards.filter((c) => c.entity_id === entityId);
  const card = cardsOfEntity.find((c) => String(c.id) === cardId);

  /** A conta escolhida, quando for de outra entidade — dispara o aviso. */
  const contaDeOutraEntidade = accountsOfOthers.find((a) => String(a.id) === accountId) ?? null;

  /**
   * Em qual CICLO a compra cai — RN-23.
   *
   * ================================================================
   * A PRÉVIA DIZ O MÊS, NÃO O DIA EXATO
   * ================================================================
   *
   * A regra do ciclo é simples e cabe aqui: até o dia do fechamento, fatura
   * deste mês; depois, a do seguinte. Sem essa prévia o usuário digita a data
   * da compra, o sistema grava outro vencimento, e ele procura o lançamento no
   * mês errado.
   *
   * O DIA exato do vencimento NÃO cabe aqui, e prometê-lo seria mentir: uma
   * fatura que cai em fim de semana ou FERIADO BANCÁRIO é empurrada para o
   * próximo dia útil (RN-24), e a tabela de feriados vive no banco. O
   * navegador não tem como saber que 10/05 é domingo e nem que 12/10 é
   * feriado.
   *
   * Então a prévia declara o mês e diz "por volta do dia N". O valor exato vem
   * na resposta do servidor, depois de salvar.
   *
   * Só datas — nenhum valor monetário é calculado aqui (ADR-0003).
   */
  /**
   * Em qual fatura a compra cai — RN-23.
   *
   * A regra mora em `lib/cardCycle`, testada, e não mais aqui dentro.
   *
   * Esta função já esteve neste arquivo e estava ERRADA EM UM MÊS: somava
   * sempre `+1` para achar o mês do vencimento, o que só vale quando o cartão
   * vence antes de fechar no calendário. Num cartão que fecha dia 7 e vence dia
   * 12, uma compra de 06/08 vence em 12/08 — e a prévia anunciava setembro.
   *
   * O backend sempre teve a regra certa, e o formulário de cartão também. Esta
   * era a única das três cópias sem teste, e foi a única que errou.
   */
  const fatura = card
    ? cardCycleFor({
        closingDay: card.closing_day,
        dueDay: card.due_day,
        purchaseDate: competenceDate,
      })
    : null;

  const categories = reference.categories.filter(
    (c) => c.selectable && (c.type === type || c.type === 'ambos'),
  );

  const save = useMutation({
    mutationFn: () => {
      if (isEdit) {
        /**
         * A edição manda só o que MUDOU.
         *
         * Reenviar o formulário inteiro gravaria uma versão nova a cada
         * salvamento, com `changed_fields` cheio de campos idênticos — e o
         * histórico do lançamento, que existe para responder "o que mexeram
         * aqui", viraria ruído.
         *
         * Tipo e entidade não vão: mudar a entidade moveria o lançamento entre
         * PF e PJ, e virar receita em despesa inverteria o resultado do
         * período. Para isso, exclua e lance de novo — que deixa rastro.
         */
        const changes: Record<string, unknown> = {};

        if (description.trim() !== transaction.description) changes.description = description.trim();
        if (competenceDate !== transaction.competence_date.slice(0, 10)) {
          changes.competence_date = competenceDate;
        }
        if (dueDate !== transaction.due_date.slice(0, 10)) changes.due_date = dueDate;
        if (Number(gross) !== Number(transaction.gross_amount)) changes.gross_amount = gross;
        if (Number(discount) !== Number(transaction.discount)) changes.discount = discount;

        const cat = categoryId === '' ? null : Number(categoryId);
        if (cat !== transaction.category_id) changes.category_id = cat;

        const acc = accountId === '' ? null : Number(accountId);
        if (acc !== transaction.account_id) changes.account_id = acc;

        const par = partyId === '' ? null : Number(partyId);
        if (par !== transaction.party_id) changes.party_id = par;

        if ((notes || null) !== transaction.notes) changes.notes = notes || null;

        return api.patch<{ meta: { warnings: string[] } }>(
          `/transactions/${transaction.id}`,
          { ...changes, ...(reason.trim() ? { reason: reason.trim() } : {}) },
          { entityIds: selectedIds },
        );
      }

      return api.post<{ meta?: { warnings?: string[] } }>('/transactions', {
        type,
        description,
        entity_id: entityId,
        competence_date: competenceDate,
        due_date: dueDate,
        gross_amount: gross,
        ...(Number(discount) > 0 ? { discount } : {}),
        ...(categoryId ? { category_id: Number(categoryId) } : {}),
        // Conta e cartão são exclusivos (CHECK `chk_fin_tx_container`): a
        // compra sai de um lugar só, e no cartão quem paga é a fatura.
        ...(cardId ? { credit_card_id: Number(cardId) } : accountId ? { account_id: Number(accountId) } : {}),
        ...(partyId ? { party_id: Number(partyId) } : {}),
        ...(installments > 1 ? { installments } : {}),
        ...(notes ? { notes } : {}),
      }, { entityIds: selectedIds });
    },
    onSuccess: (res) => onSaved(res?.meta?.warnings),
  });

  const error = save.error instanceof ApiError ? save.error : null;
  // Com baixa registrada, o motivo é parte do que torna a edição submissível:
  // sem ele o servidor recusa, e desabilitar o botão diz isso antes do clique.
  const valorMudou = isEdit && Number(gross) !== Number(transaction.gross_amount);
  const canSubmit =
    description.trim().length >= 2 &&
    Number(gross) > 0 &&
    entityId > 0 &&
    (!isSettled || !valorMudou || reason.trim().length >= 5);

  return (
    <Modal title={isEdit ? 'Editar lançamento' : 'Novo lançamento'} onClose={onClose}>
      <form
        onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
        className="space-y-4"
      >
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(error.errors).map(([field, msgs]) => (
                  <li key={field}>{msgs[0]}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Tipo */}
        <div className="flex gap-2">
          {(['despesa', 'receita'] as const).map((t) => (
            <button
              key={t}
              type="button"
              disabled={isEdit}
              onClick={() => { setType(t); setCategoryId(''); }}
              className={`flex-1 rounded-lg border px-4 py-2.5 text-sm font-medium transition ${
                type === t
                  ? t === 'receita'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                    : 'border-rose-500 bg-rose-50 text-rose-800 dark:bg-rose-950/50 dark:text-rose-300'
                  : 'border-slate-300 text-slate-600 dark:border-slate-700 dark:text-slate-400'
              } ${isEdit ? 'cursor-not-allowed opacity-60' : ''}`}
            >
              {t === 'receita' ? '↓ Receita' : '↑ Despesa'}
            </button>
          ))}
        </div>

        <Field label="Descrição" required>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            minLength={2}
            maxLength={500}
            autoFocus
            placeholder="Ex.: Energia elétrica"
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valor" required>
            <input
              value={grossMasked}
              onChange={(e) => setGrossMasked(maskMoneyInput(e.target.value))}
              required
              inputMode="numeric"
              placeholder="0,00"
              className="input w-full text-right"
            />
          </Field>
          {/* Trocar a entidade mudaria o DRE de duas entidades sozinho, e
              nada no lançamento contaria que ele veio de outra. */}
          <Field label="Entidade" required hint={isEdit ? 'não muda depois de criado' : undefined}>
            <select
              value={entityId}
              disabled={isEdit}
              onChange={(e) => { setEntityId(Number(e.target.value)); setAccountId(''); }}
              className="input w-full disabled:opacity-60"
            >
              {reference.entities.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </Field>
        </div>

        {/*
          ================================================================
          NO CARTÃO, UMA DATA SÓ
          ================================================================

          Fora do cartão são duas datas com papéis distintos: quando o gasto
          aconteceu (competência) e quando ele vence (caixa).

          No cartão, "Vencimento" não tem o que fazer: quem manda é a fatura, e
          o servidor SOBRESCREVE o que estiver aqui pelo vencimento dela. Um
          campo obrigatório, editável, que o sistema descarta em silêncio é pior
          do que campo nenhum — a pessoa escolhe uma data, confia nela, e o
          sistema grava outra sem avisar.

          Então ele some, e "Mês de referência" assume o nome que a pessoa
          espera: a data da compra. É essa que decide em qual fatura ela cai.
        */}
        <div className={card ? '' : 'grid grid-cols-2 gap-3'}>
          {!card && (
            <Field label="Vencimento" required>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => { setDueDate(e.target.value); setDueTouched(true); }}
                required
                className="input w-full"
              />

              {/* A sugestão só APARECE quando difere do que está no campo, e só
                  se aplica com um clique. Nunca troca a data por conta própria
                  depois que a pessoa mexeu nela. */}
              {suggestion && suggestion !== dueDate && (
                <button
                  type="button"
                  onClick={() => { setDueDate(suggestion); setDueTouched(true); }}
                  className="mt-1 text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                >
                  usar {formatDate(suggestion)} — {termDays} dias de prazo
                </button>
              )}
            </Field>
          )}

          <Field
            label={card ? 'Data da compra' : 'Mês de referência'}
            hint={
              card
                ? 'o dia em que você passou o cartão — é ela que define a fatura'
                : 'quando o gasto aconteceu, mesmo que pague depois'
            }
          >
            <input
              type="date"
              value={competenceDate}
              onChange={(e) => setCompetenceDate(e.target.value)}
              required
              className="input w-full"
            />
          </Field>
        </div>

        <Field label="Categoria">
          <CategorySelect
            value={categoryId}
            onChange={setCategoryId}
            type={type}
            categories={categories}
          />
        </Field>

        <Field
          label={type === 'receita' ? 'Cliente' : 'Fornecedor'}
          hint="alimenta o relatório por cliente/fornecedor"
        >
          <select
            value={partyId}
            onChange={(e) => {
              setPartyId(e.target.value);

              // Aplica o prazo do contato só enquanto a data for a padrão.
              const escolhido = parties.find((p) => String(p.id) === e.target.value);
              const dias = escolhido?.default_payment_term_days ?? null;

              if (dias !== null && !dueTouched && competenceDate) {
                setDueDate(suggestDueDate(competenceDate, dias));
              }
            }}
            className="input w-full"
          >
            <option value="">Nenhum</option>
            {parties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          {termDays !== null && (
            <p className="mt-1 text-xs text-slate-400">
              prazo padrão de {termDays} dias, contado da data de referência
            </p>
          )}
        </Field>

        {/*
          ================================================================
          DE ONDE SAI O DINHEIRO: conta OU cartão
          ================================================================

          Os dois num campo só, e não em dois, porque são mutuamente exclusivos
          — o banco recusa os dois preenchidos (`chk_fin_tx_container`). Dois
          seletores lado a lado convidariam a preencher ambos e receber um erro
          que não explica por quê.
        */}
        <Field
          label="Pago com"
          hint={
            card
              ? 'a fatura é que vence — a data acima é a da compra'
              : 'onde o dinheiro vai entrar ou sair'
          }
        >
          <select
            value={cardId ? `cartao:${cardId}` : accountId ? `conta:${accountId}` : ''}
            onChange={(e) => {
              const [tipo, id] = e.target.value.split(':');

              setCardId(tipo === 'cartao' ? (id ?? '') : '');
              setAccountId(tipo === 'conta' ? (id ?? '') : '');
            }}
            className="input w-full"
          >
            <option value="">Definir na baixa</option>

            {accountsOfEntity.length > 0 && (
              <optgroup label="Conta">
                {accountsOfEntity.map((a) => (
                  <option key={`conta-${a.id}`} value={`conta:${a.id}`}>{a.name}</option>
                ))}
              </optgroup>
            )}

            {accountsOfOthers.length > 0 && (
              <optgroup label="Contas de outra entidade">
                {accountsOfOthers.map((a) => (
                  <option key={`conta-${a.id}`} value={`conta:${a.id}`}>
                    {a.name} · {a.entity_name}
                  </option>
                ))}
              </optgroup>
            )}

            {/* Cartão só em despesa: fatura é passivo, não recebe receita. */}
            {type === 'despesa' && cardsOfEntity.length > 0 && (
              <optgroup label="Cartão de crédito">
                {cardsOfEntity.map((c) => (
                  <option key={`cartao-${c.id}`} value={`cartao:${c.id}`}>
                    {c.name}{c.last4 ? ` ····${c.last4}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>

          {/**
            * CONTA DE OUTRA ENTIDADE: registra, e diz o que está registrando.
            *
            * O mesmo aviso da tela de baixa, pela mesma razão: sem ele o caixa
            * de uma entidade sobe e o da outra não, sem nada ligando as pontas.
            */}
          {contaDeOutraEntidade && (
            <div className="mt-1 rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3 text-xs dark:border-indigo-900/50 dark:bg-indigo-950/30">
              <p className="font-medium text-indigo-900 dark:text-indigo-200">
                O dinheiro sai da conta de {contaDeOutraEntidade.entity_name}.
              </p>
              <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
                O lançamento continua sendo desta entidade, e fica registrado que{' '}
                {contaDeOutraEntidade.entity_name} pagou por ela. O saldo entre as duas aparece no
                painel e some quando você fizer a transferência de <strong>reembolso</strong>.
              </p>
            </div>
          )}

          {/**
            * COM BAIXA REGISTRADA, ESTE CAMPO MOVE DINHEIRO DE VERDADE.
            *
            * Antes ele não movia — era só a conta PREVISTA do título, e a baixa
            * ficava onde estava. Quem vinha aqui corrigir de onde pagou saía
            * com a tela dizendo um banco e o extrato mostrando outro.
            *
            * Agora corrige as duas coisas, e avisa antes: dois saldos vão mudar.
            */}
          {isEdit && isSettled && !card && (
            <p className="mt-1 rounded-lg bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              Este lançamento já tem baixa. Trocar a conta aqui <strong>move o pagamento</strong>:
              o saldo volta para a conta anterior e sai da nova.
            </p>
          )}

          {/*
            A prévia da fatura é o que torna a regra previsível.

            Sem ela, o usuário lança uma compra de hoje, o sistema grava o
            vencimento da fatura, e ele procura em A pagar deste mês sem
            encontrar — concluindo que não salvou.
          */}
          {card && fatura && (
            <p className="mt-1 rounded-lg bg-indigo-50 px-3 py-2 text-xs text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-200">
              Entra na fatura <strong>{fatura.reference}</strong> ({fatura.cycleLabel}), que vence
              em <strong>{fatura.dueLabel}</strong>, por volta do dia {card.due_day}.
              <span className="block text-indigo-700/80 dark:text-indigo-300/80">
                O cartão fecha dia {card.closing_day} — compra a partir do dia {card.closing_day + 1}{' '}
                cai na fatura seguinte. Se o vencimento cair em fim de semana ou feriado, passa para
                o dia útil seguinte.
                {installments > 1 && ' Cada parcela acompanha o vencimento da sua fatura.'}
              </span>
            </p>
          )}
        </Field>

        {/* ---------- Avançado ---------- */}
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="text-sm text-indigo-600 hover:underline dark:text-indigo-400"
        >
          {advanced ? '− Menos opções' : '+ Mais opções'}
        </button>

        {advanced && (
          <div className="space-y-4 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Desconto">
                <input
                  value={discountMasked}
                  onChange={(e) => setDiscountMasked(maskMoneyInput(e.target.value))}
                  inputMode="numeric"
                  placeholder="0,00"
                  className="input w-full text-right"
                />
              </Field>
              {/* Parcelar é criar N lançamentos, não alterar um. Oferecer o
                  campo na edição sugeriria que um lançamento existente pode
                  "virar" parcelado — e o que aconteceria é outra coisa. */}
              {!isEdit && (
                <Field label="Parcelas" hint="acima de 1 gera parcelamento">
                  <input
                    type="number"
                    min={1}
                    max={420}
                    value={installments}
                    onChange={(e) => setInstallments(Math.max(1, Number(e.target.value)))}
                    className="input w-full text-right"
                  />
                </Field>
              )}
            </div>

            <Field label="Observações">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={2000}
                className="input w-full"
              />
            </Field>
          </div>
        )}

        {/* ---------- Prévia ---------- */}
        {Number(gross) > 0 && (
          <div className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm dark:bg-slate-800/50">
            {installments > 1 ? (
              <>
                <div className="flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">Total</span>
                  <strong>{formatMoney(previewNet)}</strong>
                </div>
                <div className="mt-1 flex justify-between">
                  <span className="text-slate-500 dark:text-slate-400">{installments}x de aprox.</span>
                  <strong>{formatMoney((Number(previewNet) / installments).toFixed(2))}</strong>
                </div>
                <p className="mt-1.5 text-xs text-slate-400">
                  O servidor distribui os centavos para a soma fechar exata.
                </p>
              </>
            ) : (
              <div className="flex justify-between">
                <span className="text-slate-500 dark:text-slate-400">Valor líquido</span>
                <strong>{formatMoney(previewNet)}</strong>
              </div>
            )}
          </div>
        )}

        {/*
          Lançamento com baixa: o aviso vem ANTES do botão, não depois do erro.

          Alterar o valor de algo já pago mexe num período que pode estar
          conciliado. Deixar a pessoa preencher tudo para só então receber um
          422 seria esconder a informação até o momento em que ela mais irrita.
        */}
        {isSettled && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
            <p className="font-medium text-amber-900 dark:text-amber-200">
              Este lançamento já tem baixa de {formatMoney(transaction.settled_amount)}
            </p>
            <p className="mt-1 text-xs text-amber-800/90 dark:text-amber-300/90">
              Mudar o valor altera um período que pode estar conciliado, e o novo valor não pode
              ficar abaixo do que já foi baixado. Informe o motivo — ele fica no histórico do
              lançamento e na auditoria.
            </p>

            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              placeholder="Ex.: nota fiscal veio com valor diferente do boleto"
              className="input mt-2 w-full text-sm"
            />
          </div>
        )}

        {/**
          * ANEXOS ficam ANTES dos botões, e não numa aba separada.
          *
          * O documento é a prova do lançamento: quem está conferindo o valor
          * quer ver a nota fiscal sem sair da tela. Numa aba, ele só seria
          * visto por quem lembrasse de procurar.
          */}
        <AttachmentPanel transactionId={transaction?.id ?? null} entityIds={selectedIds} />

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : isEdit ? 'Salvar alterações' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// -------------------------------------------------------------------

export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    /*
      ================================================================
      O VÉU PRECISA DE UM VALOR PRÓPRIO NO ESCURO
      ================================================================

      `bg-slate-900/50` escurece uma página branca — é o certo no tema claro.
      No escuro ele faz o oposto: a página é `slate-950`, e slate-900 é MAIS
      CLARO que ela. O véu clareava o fundo em vez de afastá-lo, e ficava do
      mesmo tom do painel do modal — que também é slate-900. O modal deixava de
      se destacar da página atrás.

      Este defeito não é "esqueceu o `dark:`": a classe existe e é escura. Ela
      só é escura DEMAIS DE MENOS para o fundo em que passou a viver. Nenhuma
      varredura por cor clara sem contraparte encontraria isso — apareceu
      olhando a tela.
    */
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm dark:bg-black/70">
      <div className="my-8 w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-100"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
        {label}
        {required && <span className="ml-0.5 text-rose-500">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}
